import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography } from '@mui/material';
import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { isNull } from 'lodash';
import { compose } from 'redux';

import { FeeEscalationStatus } from 'constants/feeEscalations';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';

import { getLoanFeeEscalationsAction } from 'store/loanApplications/actions/loanFeeEscalations.actions';

import { schema } from './FeeEscalationsForm.schema';
import {
  cardWrapperStyles,
  fieldRowStyles,
  fieldsStyles,
  fieldVerticalRowStyles,
  getFieldValueStyles,
} from './FeeEscalationsForm.styles';
import { FeeEscalationsFormTestIds } from './FeeEscalationsForm.testIds';
import { getFeeEscalationFields, getFeeEscalationOptions } from './FeeEscalationsForm.utils';

const FeeEscalationsForm = ({ isLoading, setIsLoading, formRef, onSubmit, label }) => {
  const loanId = useLoanId();

  const dispatch = useDispatch();

  const [feeEscalationOptions, setFeeEscalationOptions] = useState(null);

  const { form, setSchema, setInForm, isFormChanged, validateForm, validateField } = useForm({});

  const isEmptyOptions = !isNull(feeEscalationOptions) && !feeEscalationOptions?.length;
  const isSingleOption = feeEscalationOptions?.length === 1;
  const isFeeEscalationSelected = !!form.feeEscalationId?.value;

  const feeEscalationFields = useMemo(() => {
    if (!isSingleOption) {
      return;
    }

    return getFeeEscalationFields(feeEscalationOptions);
  }, [feeEscalationOptions, isSingleOption]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(getLoanFeeEscalationsAction({ loanId, status: FeeEscalationStatus.PENDING }))
      .then(({ payload }) => {
        setFeeEscalationOptions(getFeeEscalationOptions(payload));

        if (payload.length === 1) {
          setInForm('feeEscalationId', { value: payload[0].reggoraFeeEscalationId });
        }
      })
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [setIsLoading, dispatch]);

  const onSubmitForm = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      onSubmit(form.feeEscalationId?.value).finally(() => setIsLoading(false));
    },
    [validateForm, form, onSubmit, setIsLoading],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="halcyon-tax-transcript-form"
      isFormChanged={isFormChanged}
      isSubmitButtonDisabled={!isFeeEscalationSelected}
      isSubmitting={isLoading}
      submitButtonName={label}
      onSubmit={isEmptyOptions ? undefined : onSubmitForm}
    >
      {isSingleOption && (
        <Box sx={cardWrapperStyles}>
          <Box sx={fieldsStyles}>
            {feeEscalationFields.map(({ name, value, isVertical, valueNode }) => (
              <Box key={name} sx={isVertical ? fieldVerticalRowStyles : fieldRowStyles}>
                <Typography color="text.secondary" variant="regular14">
                  {name}
                </Typography>
                <Box sx={getFieldValueStyles(isVertical)}>
                  {valueNode || <Typography variant="medium14">{value}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {isEmptyOptions && (
        <Typography variant="regular14">
          No open fee escalations on this loan&apos;s appraisal order.
        </Typography>
      )}

      {!isEmptyOptions && !isSingleOption && (
        <AutocompleteCDS
          disableClearable
          required
          containerClassName="mb-24"
          dataTestId={FeeEscalationsFormTestIds.FEE_ESCALATION_SELECT}
          disabled={isLoading}
          label="Fee Escalation"
          name="feeEscalationId"
          options={feeEscalationOptions}
          value={form.feeEscalationId || ''}
          onChange={onAutocompleteChange}
        />
      )}
    </ModalFormWrapper>
  );
};

FeeEscalationsForm.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(FeeEscalationsForm);
